import { useCallback, useEffect, useState } from 'react'

import { validateServiceMap } from '@/api/processing/services'
import { ServiceMapError } from '@/api/processing/types'
import { getErrorMessage, isThrottledError } from '@/utils/api'

import useEarlyUpload from './useEarlyUpload'

export type ServiceMapStatus =
  | 'idle'
  | 'uploading'
  | 'checking'
  | 'valid'
  | 'invalid'
  | 'error'

export interface UseServiceMapValidationParams {
  file: File | null
}

export interface UseServiceMapValidationResult {
  status: ServiceMapStatus
  errors: ServiceMapError[]
  uploadId: string | null
  // O arquivo a que `status`/`uploadId` se referem. O modal compara com o
  // campo: logo depois de uma troca, o `valid` ainda é do arquivo anterior.
  validatedFile: File | null
  // Mensagem da API quando o `error` é um 429; senão, `null`.
  errorDetail: string | null
  retry: () => void
  reset: () => void
}

/**
 * PSV-424 — confere o Mapa de Atendimento assim que ele é anexado.
 *
 * O esqueleto, comum ao `useSelectionConsistency`, vem do `useEarlyUpload`.
 * Há duas diferenças de propósito:
 *
 * - `error` **bloqueia**. Na escolha ele libera porque o servidor refaz a
 *   conferência e o 400 aponta o problema; aqui o 400 da criação chegaria
 *   como toast genérico, e o usuário não saberia o que corrigir no mapa.
 * - um 400/404 da conferência também invalida o upload guardado: o id não
 *   tem `ProcessingUpload` no servidor, e repetir com ele não adianta — o
 *   retry reenvia o arquivo.
 */
const useServiceMapValidation = ({
  file,
}: UseServiceMapValidationParams): UseServiceMapValidationResult => {
  const [status, setStatus] = useState<ServiceMapStatus>('idle')
  const [errors, setErrors] = useState<ServiceMapError[]>([])
  const [validatedFile, setValidatedFile] = useState<File | null>(null)
  const [errorDetail, setErrorDetail] = useState<string | null>(null)

  // Idempotente: o efeito roda a cada mudança de identidade do `File`.
  const goIdle = useCallback(() => {
    setValidatedFile(previous => (previous === null ? previous : null))
    setErrors(previous => (previous.length === 0 ? previous : []))
    setStatus(previous => (previous === 'idle' ? previous : 'idle'))
  }, [])

  const {
    uploadId,
    attempt,
    retry,
    reset,
    beginRequest,
    discardUpload,
    upload,
  } = useEarlyUpload(goIdle)

  useEffect(() => {
    // Falsy, e não só `null`: remover o arquivo no `UploadFile` grava
    // `undefined`.
    if (!file) {
      reset()
      return
    }

    const request = beginRequest()

    const run = async () => {
      try {
        const currentUploadId = await upload(file, request, {
          onNewUpload: () => setValidatedFile(null),
          onUploading: () => setStatus('uploading'),
        })
        if (currentUploadId === null) return

        setStatus('checking')

        const result = await validateServiceMap({
          serviceMapUploadId: currentUploadId,
        })

        if (!request.isCurrent()) return

        setValidatedFile(file)
        setErrors(result.errors ?? [])
        setStatus(result.valid ? 'valid' : 'invalid')
      } catch (error) {
        const httpStatus = (error as { response?: { status?: number } })
          ?.response?.status
        if (httpStatus === 400 || httpStatus === 404) {
          discardUpload(file)
        }

        if (!request.isCurrent()) return

        setValidatedFile(null)
        setErrors([])
        setErrorDetail(isThrottledError(error) ? getErrorMessage(error) : null)
        setStatus('error')
      }
    }

    run()

    return request.cancel
  }, [file, attempt, reset, beginRequest, discardUpload, upload])

  return { status, errors, uploadId, validatedFile, errorDetail, retry, reset }
}

export default useServiceMapValidation
